// Catalog.ID Client — Booklet Shared Pieces
// ============================================================================
// Shared by every page of the printed onboarding kit ("Catalog.ID Membership
// Kit", registration-restructure §5): the per-page Account header block, the
// version stamp, and the style dictionary.
//
// Design rules (plan §5, decided 2026-09-01): all text black — no grey, no red
// (bad-printer legibility). Styles therefore carry NO color at all; pdfmake's
// default ink is black.

import type { Content, StyleDictionary, TableCell, TableLayout } from 'pdfmake/interfaces';

/** Borderless label/value table with 1pt cell padding — the header block and
 *  the card tables. pdfmake's default 2pt padding costs a line per page. */
export const COMPACT_TABLE_LAYOUT: TableLayout = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingTop: () => 1,
    paddingBottom: () => 1,
    paddingLeft: () => 0,
    paddingRight: () => 4,
};

/** What kind of kit is being printed. `delegate` covers both an invited
 *  delegate's activation set and the founding delegate's own kit, which since
 *  round 5 (2026-09-03) prints as a SEPARATE document beside the organization
 *  kit. */
export type KitKind = 'personal' | 'organization' | 'delegate';

/** The personal kit's name — also the default for any caller that does not
 *  say which kind it is printing (Settings reprints). */
export const BOOKLET_TITLE = 'Catalog.ID Membership Kit';

export const ORGANIZATION_BOOKLET_TITLE = 'Catalog.ID Organization Membership Kit';

export const DELEGATE_BOOKLET_TITLE = 'Catalog.ID Delegate Membership Kit';

/** The printed name of a kit, on its cover and in every page footer. */
export function bookletTitle(kind: KitKind): string {
    if (kind === 'organization') return ORGANIZATION_BOOKLET_TITLE;
    if (kind === 'delegate') return DELEGATE_BOOKLET_TITLE;
    return BOOKLET_TITLE;
}

// ── footer geometry ─────────────────────────────────────────────────────────
// The "‹title› — page n of N" line sits at EXACTLY the same baseline height as
// the "catalog.org" line in the letterhead's footer artwork (round 5,
// 2026-09-03). Measured from design/letterhead.svg: the outlined 'l' stem of
// "catalog.org" runs 801.746–808.373 pt from the top of the A4 page, so the
// baseline is 808.373 pt from the top = 33.517 pt above the bottom edge.
// pdfmake places the footer block's top at (pageHeight − bottom margin) and
// pdfkit draws the baseline one ascender below the text node's top margin —
// so the top margin is derived here, and kit-merge.ts reuses the same numbers
// for the pages it stamps, keeping the two renderers aligned by construction.

/** pdfmake's footer text size (also used by the kit-merge stamp). */
export const FOOTER_FONT_SIZE = 8;

/** The kit title WITHOUT the "Catalog.ID " brand prefix — what the footer
 *  line and the cover print. Footer: the line sits on the same row as the
 *  letterhead's centered "catalog.org" lockup, which leaves ~186 pt of clear
 *  width to its right — the full organization title (~210 pt at 8 pt) would
 *  collide, and the brand is literally printed beside the line. Cover (design
 *  round of 2026-09-08): the title stands directly under the Catalog lockup
 *  of the cover artwork, which carries the brand. The PDF's info.title keeps
 *  the full name. */
export function shortKitTitle(title: string): string {
    return title.replace(/^Catalog\.ID /, '');
}

/** Roboto Regular (pdfmake's bundled vfs copy): hhea ascent 1900, upem 2048. */
export const ROBOTO_ASCENDER_PER_MILLE = (1900 * 1000) / 2048;

/** Baseline of the letterhead footer's "catalog.org" line, pt above the
 *  page's bottom edge (A4: 841.89 − 808.373). */
export const LETTERHEAD_FOOTER_BASELINE_FROM_BOTTOM = 33.517;

/** The booklet's bottom page margin (LETTERHEAD_PAGE_MARGINS_A4[3]), repeated
 *  here to keep this module asset-free; letterhead.test.ts pins the two. */
const PAGE_BOTTOM_MARGIN = 80;

/** Top margin of the footer text node: pushes the line down from the footer
 *  block's top so its baseline lands on the letterhead's "catalog.org" row. */
export const FOOTER_TOP_MARGIN = PAGE_BOTTOM_MARGIN
    - LETTERHEAD_FOOTER_BASELINE_FROM_BOTTOM
    - (ROBOTO_ASCENDER_PER_MILLE / 1000) * FOOTER_FONT_SIZE;

/** Account header printed on every document page (not the front page):
 *  account id on top, org info where applicable, then the Version stamp. */
export interface AccountHeaderData {
    cid: string;
    /** e.g. 'Personal', 'Organization', 'Delegate'. */
    accountTypeLabel: string;
    /** Preformatted date + time — see {@link formatVersion}. The creation time
     *  doubles as the document version. */
    version: string;
    /** Delegate pages: the organization this delegate belongs to. */
    orgCid?: string;
    /** Printed as submitted — verification status is intentionally not claimed. */
    orgDomain?: string;
    /** Delegate role within the organization (e.g. 'owner', 'admin'). */
    orgRole?: string;
}

/** Card type letter for a 1-based share index: 1 → 'A', 2 → 'B', … */
export function cardTypeLetter(shareIndex: number): string {
    return String.fromCharCode(64 + shareIndex);
}

// ── recovery sheet names ────────────────────────────────────────────────────
// ONE source for the printed name of a recovery sheet: the sheet's page title,
// the copy sheets' footer lines and the introduction's contents rows all read
// it from here (2026-09-08: the contents list names every sheet and every copy
// individually, so the strings must agree by construction).

export const RECOVERY_SHEET_TITLE_PREFIX = 'Account Recovery Sheet';

/** "Account Recovery Sheet C" — the page title of the sheet for share index 3. */
export function recoverySheetTitle(shareIndex: number): string {
    return `${RECOVERY_SHEET_TITLE_PREFIX} ${cardTypeLetter(shareIndex)}`;
}

/** "Account Recovery Sheet C — copy 1 of 2" — the footer of an extra copy,
 *  and its row in the contents list. */
export function recoverySheetCopyTitle(shareIndex: number, copyIndex: number, copies: number): string {
    return `${recoverySheetTitle(shareIndex)} — copy ${copyIndex} of ${copies}`;
}

/** True for a master recovery sheet's title (never for a grouped or copy title). */
export function isRecoverySheetTitle(title: string): boolean {
    return /^Account Recovery Sheet [A-Z]$/.test(title);
}

/** Colour of a card type's big letter — the AssetID label tint (labels/asset/
 *  asset_labels.py: HSL, saturation 0.8, lightness 0.45, hue stepped by the
 *  golden angle per index so neighbours land far apart on the wheel). The only
 *  non-black ink in the booklet (decided 2026‑09‑02); the letter is huge, so it
 *  stays legible on a greyscale printer. */
export function cardTypeColor(shareIndex: number): string {
    const GOLDEN = 0.6180339887498949;
    const hue = (((shareIndex - 1) * GOLDEN) % 1) * 360;
    return hslToHex(hue, 0.8, 0.45);
}

function hslToHex(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hp = ((h % 360) + 360) % 360 / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0, g = 0, b = 0;
    if (hp < 1) [r, g, b] = [c, x, 0];
    else if (hp < 2) [r, g, b] = [x, c, 0];
    else if (hp < 3) [r, g, b] = [0, c, x];
    else if (hp < 4) [r, g, b] = [0, x, c];
    else if (hp < 5) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    const m = l - c / 2;
    const hex = (v: number): string => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** "YYYY-MM-DD HH:mm" in local time — the booklet's version stamp format. */
export function formatVersion(d: Date): string {
    const p = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The Account header table. `extraRows` continue the same table without a
 *  gap — the recovery-card page puts its card rows there (decided 2026-09-02:
 *  no space between Version and Card type, which buys room under the QR). */
export function accountHeader(data: AccountHeaderData, extraRows: TableCell[][] = [], marginBottom = 14): Content {
    const rows: TableCell[][] = [
        [{ text: 'Account', style: 'label' }, { text: data.cid, style: 'headerCid' }],
        [{ text: 'Account type', style: 'label' }, { text: data.accountTypeLabel, style: 'value' }],
    ];
    if (data.orgCid) rows.push([{ text: 'Organization', style: 'label' }, { text: data.orgCid, style: 'value' }]);
    if (data.orgDomain) rows.push([{ text: 'Domain', style: 'label' }, { text: data.orgDomain, style: 'value' }]);
    if (data.orgRole) rows.push([{ text: 'Role', style: 'label' }, { text: data.orgRole, style: 'value' }]);
    rows.push([{ text: 'Version', style: 'label' }, { text: data.version, style: 'value' }]);
    rows.push(...extraRows);

    return {
        layout: COMPACT_TABLE_LAYOUT,
        table: { widths: [110, '*'], body: rows },
        margin: [0, 0, 0, marginBottom],
    };
}

export const BOOKLET_STYLES: StyleDictionary = {
    // ── cover (design/coverpage.svg round, 2026-09-08): everything centered ──
    coverTitle: { fontSize: 26, bold: true, alignment: 'center' },
    /** The "Account: …" / "Domain: …" / "Version: …" lines under the title. */
    coverLine: { fontSize: 15, lineHeight: 1.3, alignment: 'center' },
    /** The account id inside its cover line — bold and spaced like `headerCid`. */
    coverCid: { fontSize: 17, bold: true, characterSpacing: 1.5 },
    /** "PRINT TWICE ONLY, …" — the warning block low on the cover. */
    coverWarning: { fontSize: 18, bold: true, alignment: 'center', lineHeight: 1.2 },
    /** The storage note under the warning — sized for the cover's page budget
     *  (five lines at most, see cover-page.ts WARNING_TOP). */
    coverNote: { fontSize: 10.5, alignment: 'center', lineHeight: 1.25 },
    title: { fontSize: 20, bold: true },
    pageTitle: { fontSize: 16, bold: true, margin: [0, 0, 0, 10] },
    section: { fontSize: 12, bold: true, margin: [0, 0, 0, 6] },
    credential: { fontSize: 13, bold: true, characterSpacing: 1 },
    headerCid: { fontSize: 14, bold: true, characterSpacing: 1 },
    label: { fontSize: 9 },
    value: { fontSize: 10, lineHeight: 1.2 },
    note: { fontSize: 9, lineHeight: 1.2 },
    /** The introduction's agreement statement — the kit's ONE consent line
     *  since round 5, so it is set larger than the body copy around it. */
    consent: { fontSize: 12, bold: true, lineHeight: 1.25 },
};
